import moment from "moment";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as configActions from "src/redux/slices/configSlice";

interface InsightsProgressCardProps {
  loggerType: string;
  unit?: string;
}

const InsightsProgressCard: React.FC<InsightsProgressCardProps> = ({
  loggerType,
  unit,
}) => {
  const [goalValue, setGoalValue] = useState<number>(0);
  const [acheivedValue, setAcheivedValue] = useState<number>(0);
  const [percentage, setPercentage] = useState<number>(0);
  const [goalUnit, setGoalUnit] = useState<string>("");

  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const {
    reminders: {
      data: progressData,
      isLoading: progressLoading,
      error: progressError,
    },
  } = useAppSelector((state) => state.config);

  useEffect(() => {
    if (patient?.data?.patient_id) {
      getReminders(patient?.data?.patient_id);
    }
  }, [patient?.data?.patient_id]);

  useEffect(() => {
    if (progressData) {
      const loggerDataMap: Record<
        string,
        keyof (typeof progressData)["loggers_data"]
      > = {
        diet: "diet_data",
        exercise: "exercise_data",
        sleep: "sleep_data",
        steps: "steps_data",
        water: "water_data",
        medication: "medication_data",
      };

      const loggerKey = loggerType ? loggerDataMap[loggerType] : undefined;

      setAcheivedValue(
        loggerKey ? progressData?.loggers_data[loggerKey]?.acheived_value : 0
      );
      setGoalValue(
        loggerKey ? progressData?.loggers_data[loggerKey]?.goal_value : 0
      );
      setGoalUnit(
        loggerKey ? progressData?.loggers_data[loggerKey]?.unit : unit || ""
      );
      setPercentage(
        loggerKey
          ? (progressData?.loggers_data[loggerKey]?.acheived_value /
              progressData?.loggers_data[loggerKey]?.goal_value) *
              100
          : 0
      );
    }
  }, [progressData, loggerType]);

  const getReminders = (userId: string) => {
    new Promise((resolve, reject) => {
      dispatch(
        configActions.getRemindersRequest({
          payload: {
            user_id: userId,
            current_date: moment().format("YYYY-MM-DD"),
          },
          resolve,
          reject,
        })
      );
    });
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">
        Today's Progress
      </h3>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-1 flex-wrap">
          <span className="text-2xl font-bold text-gray-900">
            {acheivedValue?.toLocaleString() || 0}
          </span>
          <span className="text-sm text-gray-500">
            {`${goalUnit ? goalUnit + " of " : "of "}${
              goalValue?.toLocaleString() || 0
            }${goalUnit ? " " + goalUnit : ""}`}
          </span>
        </div>
        <span className="text-sm font-semibold text-gray-900 shrink-0">
          {percentage.toFixed(0)}%
        </span>
      </div>
      <div className="h-2.5 w-full bg-gray-200 rounded-full overflow-hidden">
        {progressLoading ? (
          <div className="h-full rounded-full bg-gray-200 animate-pulse" />
        ) : (
          <div
            className="h-full rounded-full bg-[var(--app-primary)] transition-all duration-300"
            style={{ width: `${percentage.toFixed(2)}%` }}
          />
        )}
      </div>
    </div>
  );
};

export default InsightsProgressCard;
